// apps/web/src/lib/painel/home/frase.ts — Fase 17.8, Plano 04 (D-10; UI-SPEC U-03 e Copywriting Contract).
//
// Frase de status da home, montada por regras, sem LLM, determinística (mesmos fatos, mesma frase), sem exclamação e sem
// emoji, até 160 caracteres. O Plano 04 entregou a frase calma dos 6 perfis; o Plano 07 acrescenta o template com fatos do
// PCP; o Plano 16, o do Faturamento; o template e o trecho de realce dos demais perfis entram nos planos daquele perfil (16, 17, 18, 19 e 20), que tipam
// e acrescentam os seus `fatos` à união `FatosHome`.
import type { FatosAlmoxarifado, FatosCompras, FatosDiretoria, FatosFaturamento, FatosPcp, FatosQualidade, PainelHomeAlmoxarifadoResponse, PerfilHome } from '@pieltec/contracts';
import { formatarMoedaCompacta, formatarNumero, mesAnterior, nomeDoMes, porExtenso } from './formato';

/** Frase em até 3 trechos: o `realce` (fato mais urgente) vira `<span class="realce">` no cabeçalho. */
export interface FraseStatus {
  readonly antes: string;
  readonly realce?: string;
  readonly depois?: string;
}

/** Frase para quando não há nada urgente (texto do Copywriting Contract). */
export const FRASE_CALMA: Readonly<Record<PerfilHome, string>> = {
  pcp: 'A produção segue no ritmo. Nenhuma remessa pede atenção hoje.',
  faturamento: 'Nenhum pedido espera por você agora.',
  almoxarifado: 'A fila está em dia e nenhum pó está abaixo do mínimo.',
  qualidade: 'Nenhuma não conformidade pede atenção hoje.',
  compras: 'Nenhum pedido de compra está atrasado.',
  diretoria: 'A operação segue sem pendências para sua decisão.',
};

/**
 * Frase calma do Almoxarifado quando o pó NÃO está medido (nenhuma cor de pó cadastrada, assinatura sem medição ou indisponível):
 * fala só da fila, nunca afirma nada sobre o mínimo (D4 da UAT: "nenhum pó está abaixo do mínimo" sem pó cadastrado engana).
 */
export const FRASE_CALMA_ALMOXARIFADO_SEM_PO = 'A fila está em dia.';

/** Fatos de todas as homes que já têm template (cresce a cada plano de perfil). */
export type FatosHome = FatosPcp | FatosAlmoxarifado | FatosCompras | FatosDiretoria | FatosFaturamento | FatosQualidade;

const minuscula = (texto: string): string => `${texto.charAt(0).toLowerCase()}${texto.slice(1)}`;

/**
 * Frase do PCP (D-10, UI-SPEC U-03): 1ª sentença = estado geral; 2ª = o fato mais urgente, em realce.
 *  - sem remessa vencendo hoje: frase calma;
 *  - `Duas remessas vencem hoje` (contagem por extenso até dez, algarismos acima) + ` e uma delas ainda está na cura.`
 *    quando `emCura` > 0; `null` (cura não identificável) e 0 não entram na frase.
 */
function frasePcp(fatos: FatosPcp): FraseStatus | null {
  const vencem = Math.trunc(fatos.remessasVencemHoje);
  if (!Number.isFinite(vencem) || vencem < 1) return null;
  const realce = `${porExtenso(vencem, 'f')} ${vencem === 1 ? 'remessa vence' : 'remessas vencem'} hoje`;
  const naCura = fatos.emCura === null ? 0 : Math.min(Math.max(Math.trunc(fatos.emCura), 0), vencem);
  let depois = '.';
  if (naCura > 0) {
    if (naCura === vencem) depois = vencem === 1 ? ' e ela ainda está na cura.' : ' e todas ainda estão na cura.';
    else depois = ` e ${minuscula(porExtenso(naCura, 'f'))} delas ainda ${naCura === 1 ? 'está' : 'estão'} na cura.`;
  }
  return { antes: 'A produção segue no ritmo. ', realce, depois };
}

/**
 * Frase do Faturamento (D-10, UI-SPEC U-18): a recusa da Omie é o fato mais urgente e vai em realce; os PVs prontos para fechar
 * vêm antes, na primeira sentença. Sem recusa, os PVs prontos viram o realce; sem nenhum dos dois, frase calma.
 * `Sete pedidos estão prontos para fechar. *Um faturamento voltou da Omie* e precisa de ajuste no cadastro.`
 */
function fraseFaturamento(fatos: FatosFaturamento): FraseStatus | null {
  const pvs = Math.trunc(fatos.pvsProntos);
  const recusas = Math.trunc(fatos.omieRecusas);
  const temPvs = Number.isFinite(pvs) && pvs >= 1;
  const temRecusa = Number.isFinite(recusas) && recusas >= 1;
  if (!temPvs && !temRecusa) return null;
  const textoPvs = `${porExtenso(pvs, 'm')} ${pvs === 1 ? 'pedido está pronto' : 'pedidos estão prontos'} para fechar`;
  if (!temRecusa) return { antes: '', realce: textoPvs, depois: '.' };
  return {
    antes: temPvs ? `${textoPvs}. ` : '',
    realce: `${porExtenso(recusas, 'm')} ${recusas === 1 ? 'faturamento voltou' : 'faturamentos voltaram'} da Omie`,
    depois: recusas === 1 ? ' e precisa de ajuste no cadastro.' : ' e precisam de ajuste no cadastro.',
  };
}

/**
 * Frase do Almoxarifado (D-10, UI-SPEC U-19): o pó abaixo do mínimo é o fato mais urgente e vai em realce; os pedidos na fila vêm
 * antes, na primeira sentença, e a última cabine que pediu fecha a frase (só enquanto há pedido na fila). Sem pó abaixo do mínimo, os
 * pedidos viram o realce; sem nenhum dos dois, frase calma.
 * `Quatro pedidos de pó estão na fila. *O pó RAL 7035 está abaixo do mínimo* e a F2 acabou de pedir mais.`
 */
function fraseAlmoxarifado(fatos: FatosAlmoxarifado): FraseStatus | null {
  const fila = Math.trunc(fatos.pedidosNaFila);
  const temFila = Number.isFinite(fila) && fila >= 1;
  const po = fatos.poAbaixoMinimo === null || fatos.poAbaixoMinimo.trim() === '' ? null : fatos.poAbaixoMinimo.trim();
  if (!temFila && po === null) return null;
  const cabine = fatos.ultimaCabineSolicitante === null || fatos.ultimaCabineSolicitante.trim() === '' ? null : fatos.ultimaCabineSolicitante.trim();
  const textoFila = `${porExtenso(fila, 'm')} ${fila === 1 ? 'pedido de pó está' : 'pedidos de pó estão'} na fila`;
  const depois = temFila && cabine !== null ? ` e a ${cabine} acabou de pedir mais.` : '.';
  if (po === null) return { antes: '', realce: textoFila, depois };
  return { antes: temFila ? `${textoFila}. ` : '', realce: `O pó ${po} está abaixo do mínimo`, depois };
}

/**
 * Frase da Qualidade (D-10, UI-SPEC U-20): as NCs abertas há mais de `limiteDias` são o fato mais urgente e vão em realce; a contagem
 * das abertas vem antes, na primeira sentença. Se todas as abertas são antigas, o realce leva a frase inteira. Sem antigas, as abertas
 * viram o realce; sem nenhuma, frase calma. O limite vem da resposta, nunca de um literal.
 * `Há 5 não conformidades abertas. *Duas estão abertas há mais de 7 dias*.`
 */
function fraseQualidade(fatos: FatosQualidade): FraseStatus | null {
  const abertas = Math.trunc(fatos.ncsAbertas);
  if (!Number.isFinite(abertas) || abertas < 1) return null;
  const antigas = Math.min(Math.max(Math.trunc(fatos.ncsAntigas), 0), abertas);
  const limite = Math.trunc(fatos.limiteDias);
  const dias = `há mais de ${formatarNumero(limite, 0)} ${limite === 1 ? 'dia' : 'dias'}`;
  const nc = (n: number): string => `${n === 1 ? 'não conformidade' : 'não conformidades'}`;
  if (antigas === 0 || !Number.isFinite(limite)) {
    return { antes: '', realce: `${porExtenso(abertas, 'f')} ${nc(abertas)} ${abertas === 1 ? 'está aberta' : 'estão abertas'}`, depois: '.' };
  }
  if (antigas === abertas) {
    return { antes: '', realce: `${porExtenso(antigas, 'f')} ${nc(antigas)} ${antigas === 1 ? 'está aberta' : 'estão abertas'} ${dias}`, depois: '.' };
  }
  return {
    antes: `Há ${formatarNumero(abertas, 0)} ${nc(abertas)} ${abertas === 1 ? 'aberta' : 'abertas'}. `,
    realce: `${porExtenso(antigas, 'f')} ${antigas === 1 ? 'está aberta' : 'estão abertas'} ${dias}`,
    depois: '.',
  };
}

/**
 * Frase de Compras (D-10, UI-SPEC U-21): os pedidos de compra atrasados são o fato mais urgente e a frase inteira vai em realce, por
 * extenso até dez e em algarismos acima, com a concordância no singular e no plural. Nenhum atrasado: frase calma.
 * `*Três pedidos de compra estão atrasados*.`
 */
function fraseCompras(fatos: FatosCompras): FraseStatus | null {
  const atrasados = Math.trunc(fatos.pedidosAtrasados);
  if (!Number.isFinite(atrasados) || atrasados < 1) return null;
  return { antes: '', realce: `${porExtenso(atrasados, 'm')} ${atrasados === 1 ? 'pedido de compra está atrasado' : 'pedidos de compra estão atrasados'}`, depois: '.' };
}

const FORMATO_ANO_MES_SP = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit' });

/** 'AAAA-MM' do instante em São Paulo (o mesmo fuso do servidor); usado só quando a resposta não traz o mês da comparação. */
function mesCorrenteSaoPaulo(agora: Date): string {
  const partes = FORMATO_ANO_MES_SP.formatToParts(agora);
  const valor = (tipo: string): string => partes.find((parte) => parte.type === tipo)?.value ?? '';
  return `${valor('year')}-${valor('month')}`;
}

const capitalizar = (texto: string): string => `${texto.charAt(0).toUpperCase()}${texto.slice(1)}`;

/**
 * Frase da Diretoria (D-10, UI-SPEC U-03). Com valores e base de comparação: o mês contra o mesmo ponto do mês anterior e a projeção do
 * mês; quando o mês está abaixo, o realce diz se a carteira cobre a diferença. Sem R$ (ou sem base de comparação), as decisões pendentes
 * viram o realce; sem nada, frase calma. O mês vem da resposta (`mesReferencia`); sem ele vale o mês corrente de São Paulo.
 * `Outubro está 13% abaixo de setembro no mesmo ponto, *mas a carteira cobre a diferença*: a projeção do mês é de R$ 316 mil.`
 */
function fraseDiretoria(fatos: FatosDiretoria, agora: Date): FraseStatus | null {
  const variacao = fatos.variacaoMesPct;
  if (variacao !== undefined && Number.isFinite(variacao) && fatos.projecaoMes !== undefined) {
    const referencia = fatos.mesReferencia ?? mesCorrenteSaoPaulo(agora);
    const mes = capitalizar(nomeDoMes(referencia));
    const anterior = nomeDoMes(mesAnterior(referencia));
    const pontos = Math.round(Math.abs(variacao));
    const depois = `: a projeção do mês é de ${formatarMoedaCompacta(fatos.projecaoMes)}.`;
    if (pontos === 0) return { antes: '', realce: `${mes} está igual a ${anterior} no mesmo ponto`, depois };
    if (variacao > 0) return { antes: '', realce: `${mes} está ${formatarNumero(pontos, 0)}% acima de ${anterior} no mesmo ponto`, depois };
    const abaixo = `${mes} está ${formatarNumero(pontos, 0)}% abaixo de ${anterior} no mesmo ponto`;
    if (fatos.carteiraCobre === undefined) return { antes: '', realce: abaixo, depois };
    return {
      antes: `${abaixo}, `,
      realce: fatos.carteiraCobre ? 'mas a carteira cobre a diferença' : 'e a carteira não cobre a diferença',
      depois,
    };
  }
  const decisoes = Math.trunc(fatos.decisoesPendentes);
  if (!Number.isFinite(decisoes) || decisoes < 1) return null;
  return { antes: '', realce: `${porExtenso(decisoes, 'm')} ${decisoes === 1 ? 'ponto pede' : 'pontos pedem'} sua decisão`, depois: ' hoje.' };
}

/** O que a frase sabe além dos fatos: hoje, se o pó está medido (a assinatura "Pó em estoque" MEDIDA com pelo menos uma cor). */
export interface ContextoFrase {
  /** `false` = sem pó medido (a frase calma do Almoxarifado não menciona o mínimo); ausente = não informado (frase calma completa). */
  readonly poMedido?: boolean;
}

/**
 * O pó está medido? Só quando a assinatura "Pó em estoque" veio MEDIDA com pelo menos uma cor: sem pó cadastrado (SEM_MEDICAO, lista
 * vazia), indisponível ou ainda carregando (`undefined`) a frase não pode dizer nada sobre o mínimo.
 */
export function poEstaMedido(assinatura: PainelHomeAlmoxarifadoResponse['assinatura'] | undefined): boolean {
  return assinatura !== undefined && assinatura.estado === 'MEDIDO' && assinatura.tipo === 'po-estoque' && assinatura.cores.length >= 1;
}

/**
 * Frase do perfil. Sem fatos urgentes (ou sem fatos) devolve a frase calma; cada plano de perfil acrescenta o seu ramo.
 */
export function montarFrase(perfil: PerfilHome, fatos?: FatosHome, agora: Date = new Date(), contexto: ContextoFrase = {}): FraseStatus {
  if (perfil === 'pcp' && fatos?.perfil === 'pcp') {
    const frase = frasePcp(fatos);
    if (frase !== null) return frase;
  }
  if (perfil === 'faturamento' && fatos?.perfil === 'faturamento') {
    const frase = fraseFaturamento(fatos);
    if (frase !== null) return frase;
  }
  if (perfil === 'almoxarifado' && fatos?.perfil === 'almoxarifado') {
    const frase = fraseAlmoxarifado(fatos);
    if (frase !== null) return frase;
  }
  if (perfil === 'almoxarifado' && contexto.poMedido === false) return { antes: FRASE_CALMA_ALMOXARIFADO_SEM_PO };
  if (perfil === 'qualidade' && fatos?.perfil === 'qualidade') {
    const frase = fraseQualidade(fatos);
    if (frase !== null) return frase;
  }
  if (perfil === 'compras' && fatos?.perfil === 'compras') {
    const frase = fraseCompras(fatos);
    if (frase !== null) return frase;
  }
  if (perfil === 'diretoria' && fatos?.perfil === 'diretoria') {
    const frase = fraseDiretoria(fatos, agora);
    if (frase !== null) return frase;
  }
  return { antes: FRASE_CALMA[perfil] };
}
